import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'

interface State {
  error: Error | null
}

/**
 * Contains a rendering error to the wave planning content, so the app header
 * and navigation stay usable. Remount it (``key``) to reset on navigation.
 */
export class WavePlanErrorBoundary extends Component<
  { children: ReactNode },
  State
> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Wave planning view failed to render', error, info)
  }

  render() {
    if (!this.state.error) return this.props.children
    return (
      <Card
        role="alert"
        className="m-6 flex flex-col items-center gap-3 p-12 text-center shadow-none"
      >
        <AlertTriangle size={28} className="text-destructive" />
        <h2 className="text-lg font-semibold">This view could not be shown</h2>
        <p className="max-w-md text-sm text-muted-foreground">
          Something unexpected happened while rendering it. Reload to try again;
          your saved changes are not affected.
        </p>
        <Button onClick={() => window.location.reload()}>Reload</Button>
      </Card>
    )
  }
}
